import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Briefcase, CalendarDays, Send, CheckCircle } from "lucide-react";
import { useLang } from "@/LanguageContext";
import t from "@/translations";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KarrierePage() {
  const { lang } = useLang();
  const tx = t[lang];
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyJob, setApplyJob] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const formTs = useRef(0);

  useEffect(() => {
    axios.get(`${API}/jobs`).then(({ data }) => setJobs(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openApply = (job) => {
    setApplyJob(job);
    setForm({ name: "", email: "", phone: "", message: "" });
    setHoneypot("");
    formTs.current = Math.floor(Date.now() / 1000);
    setSent(false);
    setError("");
  };

  const handleApply = async () => {
    setSending(true);
    setError("");
    try {
      await axios.post(`${API}/jobs/${applyJob.id}/apply`, { ...form, _website: honeypot, _ts: formTs.current });
      setSent(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : (lang === "de" ? "Fehler beim Senden." : "Failed to send."));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });

  return (
    <div data-testid="karriere-page" className="min-h-screen bg-[#0C1620]">
      <Header />
      <main className="pt-20 sm:pt-24 pb-16 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 sm:mb-16">
            <p className="uppercase text-[10px] sm:text-xs tracking-[0.2em] text-[#C5993A] mb-3">
              {tx.nav_karriere}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white font-light mb-4">
              {tx.karriere_h1}
            </h1>
            <p className="text-[#8899AA] text-sm sm:text-base font-light max-w-2xl">
              {tx.karriere_sub}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="w-8 h-8 border-2 border-[#C5993A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16 bg-[#12202f] border border-[#3A4F60]/30 rounded-lg">
              <Briefcase className="w-12 h-12 text-[#3A4F60] mx-auto mb-4" />
              <p className="text-[#8899AA]">{tx.karriere_no_jobs}</p>
            </div>
          ) : (
            <div className="space-y-8" data-testid="jobs-list">
              {jobs.map((job) => {
                const title = lang === "de" ? job.title_de : job.title_en;
                const sections = job.sections || [];
                return (
                  <div key={job.id} data-testid={`job-${job.id}`} className="bg-[#12202f] border border-[#3A4F60]/50 rounded-lg overflow-hidden">
                    <div className="p-6 sm:p-8 lg:p-10">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
                        <h2 className="font-heading text-2xl sm:text-3xl text-white font-light">{title}</h2>
                        <span className="flex items-center gap-1.5 text-xs text-[#8899AA] flex-shrink-0">
                          <CalendarDays className="w-3.5 h-3.5 text-[#C5993A]" />
                          {tx.karriere_published}: {formatDate(job.publish_date)}
                        </span>
                      </div>

                      <div className="space-y-6">
                        {sections.map((sec, i) => {
                          const secTitle = lang === "de" ? sec.title_de : sec.title_en;
                          const secContent = lang === "de" ? sec.content_de : sec.content_en;
                          return (
                            <div key={i}>
                              <h3 className="font-heading text-lg sm:text-xl text-[#C5993A] mb-2">{secTitle}</h3>
                              <div className="text-[#8899AA] text-sm sm:text-base font-light leading-relaxed whitespace-pre-line">{secContent}</div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-8 pt-6 border-t border-[#3A4F60]/30">
                        <Button
                          data-testid={`apply-job-${job.id}`}
                          onClick={() => openApply(job)}
                          className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white font-medium px-8 py-5 text-sm rounded hover:from-[#8B6B20] hover:to-[#C5993A] transition-all border-0"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          {tx.karriere_apply}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />

      {/* Apply Dialog */}
      <Dialog open={!!applyJob} onOpenChange={(o) => { if (!o) setApplyJob(null); }}>
        <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-lg" aria-describedby="apply-desc">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {sent ? tx.karriere_success_title : tx.karriere_apply_title}
            </DialogTitle>
            <p id="apply-desc" className="sr-only">Application form</p>
          </DialogHeader>
          {sent ? (
            <div className="text-center py-6" data-testid="apply-success">
              <CheckCircle className="w-14 h-14 text-[#C5993A] mx-auto mb-4" />
              <p className="text-[#8899AA] text-sm">{tx.karriere_success_text}</p>
              <Button onClick={() => setApplyJob(null)} className="mt-4 bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">OK</Button>
            </div>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
                  <input type="text" name="_website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} autoComplete="off" tabIndex={-1} />
                </div>
                <div className="bg-[#0C1620] rounded-lg p-3 border border-[#3A4F60]/30">
                  <p className="text-white text-sm font-medium">{lang === "de" ? applyJob?.title_de : applyJob?.title_en}</p>
                </div>
                <div><label className="block text-xs text-[#8899AA] mb-1">{tx.karriere_name}</label><Input data-testid="apply-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1">{tx.karriere_email}</label><Input data-testid="apply-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1">{tx.karriere_phone}</label><Input data-testid="apply-phone" type="tel" required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A]" /></div>
                <div><label className="block text-xs text-[#8899AA] mb-1">{tx.karriere_message}</label><Textarea data-testid="apply-message" required value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} rows={4} className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-sm focus:border-[#C5993A] resize-none" /></div>
                {error && <p className="text-red-400 text-xs">{error}</p>}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setApplyJob(null)} className="text-[#8899AA] text-sm">{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                <Button data-testid="submit-apply" onClick={handleApply} disabled={sending || !form.name || !form.email || !form.phone || !form.message} className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0 text-sm">
                  {sending ? "..." : tx.karriere_submit}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
